import { initializeApp } from "firebase/app";
import { getAuth, setPersistence, browserLocalPersistence, GoogleAuthProvider } from "firebase/auth";
import { initializeFirestore } from "firebase/firestore";
import { getFunctions } from "firebase/functions";
import { getStorage } from "firebase/storage";
import { logInfo, logError, logWarn } from "@/utils/logUtils";

// Debug: Log all available env variables
logInfo('Available env variables:', {
  mode: import.meta.env.MODE,
  dev: import.meta.env.DEV,
  prod: import.meta.env.PROD,
  base: import.meta.env.BASE_URL,
});

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID
};

// Debug: Log the config values
logInfo('Firebase Config:', {
  apiKey: firebaseConfig.apiKey ? 'present' : 'missing',
  authDomain: firebaseConfig.authDomain,
  projectId: firebaseConfig.projectId,
  storageBucket: firebaseConfig.storageBucket,
  messagingSenderId: firebaseConfig.messagingSenderId,
  appId: firebaseConfig.appId,
});

// Check if all required Firebase config values are present
const isFirebaseConfigValid = Object.values(firebaseConfig).every(value => value !== undefined && value !== '');

if (!isFirebaseConfigValid) {
  const missingKeys = Object.entries(firebaseConfig)
    .filter(([_, value]) => !value)
    .map(([key]) => key);
    
  logError('Firebase configuration is incomplete. Required values are missing:', missingKeys);
  throw new Error('Firebase configuration is incomplete. Check your environment variables.');
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firestore with offline persistence enabled
const firestoreSettings = {
  experimentalForceLongPolling: true,
  useFetchStreams: false, // Disable QUIC protocol to avoid ERR_QUIC_PROTOCOL_ERROR
  cacheSizeBytes: 100000000, // 100MB cache size
};

const db = initializeFirestore(app, firestoreSettings);

// Initialize Auth with persistence
const auth = getAuth(app);
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
  prompt: 'select_account'
});

// Initialize Storage
const storage = getStorage(app);

// Initialize Functions
const functions = getFunctions(app, import.meta.env.VITE_FIREBASE_REGION);

// Set auth persistence to LOCAL
setPersistence(auth, browserLocalPersistence)
  .then(() => logInfo("Firebase Auth persistence set to LOCAL"))
  .catch((error) => logError("Error setting auth persistence:", error));

logInfo('Mode:', import.meta.env.MODE);
logInfo('Environment:', import.meta.env.VITE_FIREBASE_ENVIRONMENT);

/**
 * Properly encode a Firebase Storage URL to handle special characters
 * @param storageUrl - The original storage URL
 * @returns An encoded URL that will work reliably
 */
export const encodeStorageUrl = (storageUrl: string): string => {
  try {
    // First, check if it's already a properly formed URL
    const url = new URL(storageUrl);
    
    // For Firebase Storage URLs, handle special encoding
    if (url.hostname.includes('firebasestorage.googleapis.com')) {
      // Extract the path portion (everything after /o/)
      const parts = url.pathname.split('/o/');
      if (parts.length !== 2) {
        return storageUrl; // Not the expected format, return as is
      }
      
      // Get bucket name from URL path (not hostname)
      // The format is /v0/b/BUCKET_NAME/o/FILE_PATH
      const bucketPath = parts[0].split('/b/');
      if (bucketPath.length !== 2) {
        return storageUrl; // Not the expected format, return as is
      }
      
      // Keep the original bucket name exactly as it is - don't modify it
      const bucketName = bucketPath[1];
      
      // Properly encode the file path part only - not the query parameters
      const encodedFilePath = encodeURIComponent(decodeURIComponent(parts[1]));
      
      // Reconstruct the URL correctly
      return `${url.protocol}//${url.hostname}/v0/b/${bucketName}/o/${encodedFilePath}${url.search}`;
    }
    
    // For other URLs, use the original logic
    const pathStart = url.href.indexOf(url.pathname);
    const queryStart = url.href.indexOf('?', pathStart);
    
    // Split into three parts: base URL, path, and query parameters
    const baseUrl = url.href.substring(0, pathStart);
    const path = queryStart !== -1 
      ? url.href.substring(pathStart, queryStart) 
      : url.href.substring(pathStart);
    const query = queryStart !== -1 
      ? url.href.substring(queryStart) 
      : '';
    
    // Properly encode the path - this is where spaces and special characters occur
    const encodedPath = path.split('/')
      .map(segment => encodeURIComponent(decodeURIComponent(segment)))
      .join('/');
    
    // Reassemble the URL
    return `${baseUrl}${encodedPath}${query}`;
  } catch (error) {
    // If URL parsing fails, do a simple encoding
    logError('Error encoding storage URL:', error);
    return storageUrl;
  }
};

export { app, auth, db, functions, storage, googleProvider };